/**
 * R14-D — Rule editor form-builder + R14-E bots toggle panel.
 *
 * Side-panel UI that the Keyholder (or cohost) opens from the Manage menu
 * to author per-bot rules. Form-driven (NOT a node graph).
 *
 * Props:
 *   - roomId    (string)
 *   - roomMeta  (object)  — passed through for header context
 *   - botToggles (object) — { message_bot, camera_bot, ... }
 *   - onClose   () => void
 *
 * Spec testids:
 *   - rules-editor-{bot_slug}
 *   - rules-add-{bot_slug}
 *   - rules-row-{rule_id}
 *   - rule-form-trigger
 *   - rule-form-action
 *   - rule-form-save
 *   - bots-toggle-{name}
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import DarkSelect from "./DarkSelect";
import {
  X, Plus, Edit3, Trash2, Save, Bot, Camera, MessageSquareWarning, UserCog,
  ShieldOff, ShieldAlert, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const BOTS = [
  { slug: "message_bot",  label: "MessageBot",  icon: MessageSquareWarning,
    desc: "Welcomes, scheduled announcements, and chat command replies.",
    actions: ["post_message", "dm_user"] },
  { slug: "camera_bot",   label: "CameraBot",   icon: Camera,
    desc: "Watches each participant's own cam state, escalates persistent cam-off.",
    actions: ["warn_user", "escalate_removal"] },
  { slug: "rename_bot",   label: "RenameBot",   icon: UserCog,
    desc: "DMs participants with offensive or impersonating display names; escalates if unchanged.",
    actions: ["warn_user", "escalate_removal"] },
  { slug: "no_spam_bot",  label: "NoSpamBot",   icon: ShieldOff,
    desc: "Rate-limits chat bursts, duplicate floods, and blocked link domains.",
    actions: ["rate_limit_chat", "escalate_removal"] },
  { slug: "no_swarm_bot", label: "NoSwarmBot",  icon: ShieldAlert,
    desc: "Coming Soon — Detects coordinated raids and locks the room.",
    actions: [], comingSoon: true },
  { slug: "auth_bot",     label: "AuthBot",     icon: Bot,
    desc: "Coming Soon — Discord role-based auth gating for co-host promotion.",
    actions: [], comingSoon: true },
];

const TRIGGER_TYPES = [
  { value: "participant_joined", label: "When a participant joins", bots: ["message_bot"] },
  { value: "schedule",           label: "Every N minutes",          bots: ["message_bot"] },
  { value: "chat_pattern",       label: "When chat matches pattern",bots: ["message_bot"] },
  { value: "camera_state",       label: "When camera state persists",bots: ["camera_bot"] },
  { value: "display_name_violation", label: "Bad display name (banned word or impersonation)", bots: ["rename_bot"] },
  { value: "chat_burst",         label: "Chat burst rate-limit",          bots: ["no_spam_bot"] },
  { value: "chat_dup",           label: "Duplicate message flood",        bots: ["no_spam_bot"] },
  { value: "chat_link_block",    label: "Blocked-domain link in chat",    bots: ["no_spam_bot"] },
  { value: "chat_strikes",       label: "N spam strikes in window",       bots: ["no_spam_bot"] },
];

const ACTION_TYPES = [
  { value: "post_message",     label: "Post message in public chat",  textRequired: true },
  { value: "dm_user",          label: "DM the triggering user",        textRequired: true },
  { value: "warn_user",        label: "Send a warning to the user",    textRequired: true },
  { value: "rate_limit_chat",  label: "Rate-limit user's chat (N seconds)", textRequired: true },
  { value: "escalate_removal", label: "Escalate removal to staff",     textRequired: false },
];


export default function RulesEditor({ roomId, roomMeta, botToggles: initialToggles = {}, onClose }) {
  const [toggles, setToggles] = useState(initialToggles);
  const [rules, setRules] = useState([]);
  const [activeBot, setActiveBot] = useState("message_bot");
  const [editingRule, setEditingRule] = useState(null);  // { ...rule, _isNew: bool }
  const [loading, setLoading] = useState(true);
  // R17-D — Host-customizable bot display names. Loaded from the room doc,
  // edited via local state, persisted via PATCH /api/rooms/{id}/bot-display-names.
  const [botNames, setBotNames] = useState(roomMeta?.bot_display_names || {});
  const [botNamesDirty, setBotNamesDirty] = useState(false);
  const [botNamesSaving, setBotNamesSaving] = useState(false);

  useEffect(() => {
    setBotNames(roomMeta?.bot_display_names || {});
    setBotNamesDirty(false);
  }, [roomMeta?.bot_display_names]);

  const setBotName = (slug, value) => {
    setBotNames((p) => ({ ...p, [slug]: value }));
    setBotNamesDirty(true);
  };

  const saveBotNames = async () => {
    setBotNamesSaving(true);
    try {
      // Only send non-empty + trimmed entries. Empty = revert to default
      // (backend treats empty string as "clear this key").
      const payload = {};
      for (const b of BOTS) {
        const v = (botNames[b.slug] || "").trim();
        payload[b.slug] = v;
      }
      const r = await fetch(`${API}/api/rooms/${encodeURIComponent(roomId)}/bot-display-names`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || "Couldn't save bot names");
      } else {
        const j = await r.json();
        setBotNames(j.bot_display_names || {});
        setBotNamesDirty(false);
        toast.success("Bot names saved");
      }
    } catch (err) {
      console.error("[rules] save bot names failed", err);
      toast.error("Couldn't save bot names");
    } finally {
      setBotNamesSaving(false);
    }
  };

  const fetchRules = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/rooms/${encodeURIComponent(roomId)}/rules`, { credentials: "include" });
      if (!r.ok) { setLoading(false); return; }
      const j = await r.json();
      setRules(j.rules || []);
      setLoading(false);
    } catch (err) {
      console.error("[rules] list failed", err);
      toast.error("Failed to load rules");
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const rulesForActiveBot = useMemo(
    () => rules.filter((r) => r.bot === activeBot),
    [rules, activeBot]
  );

  const toggleBot = async (slug, next) => {
    setToggles((t) => ({ ...t, [slug]: next }));
    try {
      const r = await fetch(`${API}/api/rooms/${encodeURIComponent(roomId)}/bot-toggles`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [slug]: next }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || "Toggle failed");
        setToggles((t) => ({ ...t, [slug]: !next }));
      }
    } catch (err) {
      console.error("[rules] toggle failed", err);
      toast.error("Toggle failed");
      setToggles((t) => ({ ...t, [slug]: !next }));
    }
  };

  const startAddRule = (bot) => {
    setEditingRule({
      _isNew: true,
      bot,
      name: "",
      enabled: true,
      trigger: { type: TRIGGER_TYPES.find((t) => t.bots.includes(bot))?.value || "participant_joined" },
      conditions: [],
      action: { type: BOTS.find((b) => b.slug === bot)?.actions[0] || "post_message", text: "" },
    });
  };

  const saveRule = async (rule) => {
    try {
      const isNew = rule._isNew;
      const url = isNew
        ? `${API}/api/rooms/${encodeURIComponent(roomId)}/rules`
        : `${API}/api/rooms/${encodeURIComponent(roomId)}/rules/${rule.rule_id}`;
      const method = isNew ? "POST" : "PATCH";
      const r = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bot: rule.bot, name: rule.name, enabled: rule.enabled,
          trigger: rule.trigger, conditions: rule.conditions, action: rule.action,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || "Save failed");
        return;
      }
      toast.success(isNew ? "Rule created" : "Rule updated");
      setEditingRule(null);
      fetchRules();
    } catch (err) {
      console.error("[rules] save failed", err);
      toast.error("Save failed");
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      const r = await fetch(`${API}/api/rooms/${encodeURIComponent(roomId)}/rules/${rule.rule_id}`, {
        method: "DELETE", credentials: "include",
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || "Delete failed");
        return;
      }
      toast.success("Rule deleted");
      fetchRules();
    } catch (err) {
      console.error("[rules] delete failed", err);
      toast.error("Delete failed");
    }
  };

  const activeBotDef = BOTS.find((b) => b.slug === activeBot) || BOTS[0];

  return (
    <div className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm flex" data-testid="rules-editor-root">
      <div className="ml-auto w-full max-w-3xl h-full bg-[#0a0a14] border-l border-white/10 flex flex-col">
        <header className="px-6 py-4 border-b border-white/8 flex items-center gap-3">
          <Bot className="w-5 h-5 text-[#00F0FF]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">Bots & Rules</div>
            <h2 className="text-base font-bold">{roomMeta?.name || "Room"}</h2>
          </div>
          <button onClick={onClose} className="ml-auto text-white/55 hover:text-white" data-testid="rules-editor-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex flex-1 overflow-hidden">
          {/* Bots sidebar */}
          <nav className="w-44 border-r border-white/8 p-2 overflow-y-auto shrink-0">
            {BOTS.map((b) => {
              const Icon = b.icon;
              const isOn = toggles[b.slug] !== false;
              const customLabel = (botNames[b.slug] || "").trim();
              return (
                <button
                  key={b.slug}
                  onClick={() => !b.comingSoon && setActiveBot(b.slug)}
                  disabled={b.comingSoon}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded text-left text-xs transition ${
                    b.comingSoon ? "opacity-40 cursor-not-allowed" :
                    activeBot === b.slug ? "bg-[#00F0FF]/12 text-[#00F0FF]" : "text-white/65 hover:bg-white/[0.04]"
                  }`}
                  data-testid={`rules-bot-tab-${b.slug}`}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate" title={customLabel || b.label}>{customLabel || b.label}</span>
                  {b.comingSoon
                    ? <span className="ml-auto text-[8px] uppercase tracking-wider bg-white/10 text-white/50 px-1.5 py-0.5 rounded">Soon</span>
                    : <span className={`ml-auto w-2 h-2 rounded-full ${isOn ? "bg-emerald-400" : "bg-white/15"}`} />}
                </button>
              );
            })}

            {/* R17-D — Friendly bot names. Lives at the bottom of the sidebar
                so it's always reachable regardless of which bot is currently
                selected in the right-pane editor. */}
            <div className="mt-4 pt-3 border-t border-white/8" data-testid="bot-display-names-panel">
              <div className="text-[9px] uppercase tracking-[0.18em] text-white/40 font-bold mb-2 px-1.5">
                Friendly bot names
              </div>
              <div className="space-y-1.5 px-1">
                {BOTS.map((b) => (
                  <div key={`name-${b.slug}`} className="flex flex-col gap-0.5">
                    <label className="text-[9px] uppercase tracking-[0.14em] text-white/35">{b.label}</label>
                    <input
                      type="text"
                      value={botNames[b.slug] || ""}
                      onChange={(e) => setBotName(b.slug, e.target.value)}
                      placeholder={b.label}
                      maxLength={48}
                      className="px-1.5 py-1 text-[11px] bg-[#0c0c12] border border-white/10 rounded text-white placeholder-white/30 focus:border-[#00F0FF]/60 focus:outline-none"
                      data-testid={`bot-display-name-input-${b.slug}`}
                    />
                  </div>
                ))}
                <button
                  onClick={saveBotNames}
                  disabled={!botNamesDirty || botNamesSaving}
                  className="mt-1.5 w-full px-2 py-1.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="bot-display-names-save"
                >
                  {botNamesSaving ? "Saving…" : botNamesDirty ? "Save names" : "Saved"}
                </button>
                <p className="text-[9px] text-white/30 leading-tight px-0.5">
                  Empty = use default name.
                </p>
              </div>
            </div>
          </nav>

          {/* Main editor pane */}
          <div className="flex-1 overflow-y-auto p-5" data-testid={`rules-editor-${activeBot}`}>
            {!editingRule ? (
              <>
                <BotHeader bot={activeBotDef} on={toggles[activeBot] !== false} onToggle={(v) => toggleBot(activeBot, v)} />

                <div className="flex items-center justify-between mt-6 mb-2">
                  <h3 className="text-xs uppercase tracking-[0.18em] text-white/55 font-bold">
                    Rules ({rulesForActiveBot.length})
                  </h3>
                  {activeBotDef.actions.length > 0 && (
                    <button
                      onClick={() => startAddRule(activeBot)}
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/12"
                      data-testid={`rules-add-${activeBot}`}
                    >
                      <Plus className="w-3 h-3" /> Add Rule
                    </button>
                  )}
                </div>

                {loading ? (
                  <div className="text-white/45 text-sm py-6 text-center">Loading…</div>
                ) : rulesForActiveBot.length === 0 ? (
                  <div className="rounded-lg border border-white/10 p-6 text-white/45 text-xs text-center">
                    {activeBotDef.actions.length === 0
                      ? "This bot does not accept rules yet."
                      : "No rules. Click \"Add Rule\" to create one."}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {rulesForActiveBot.map((r) => (
                      <RuleRow key={r.rule_id} rule={r}
                        onEdit={() => setEditingRule({ ...r, _isNew: false })}
                        onDelete={() => deleteRule(r)}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <RuleForm
                rule={editingRule}
                onChange={setEditingRule}
                onCancel={() => setEditingRule(null)}
                onSave={() => saveRule(editingRule)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function BotHeader({ bot, on, onToggle }) {
  const Icon = bot.icon;
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-md grid place-items-center bg-[#00F0FF]/10 text-[#00F0FF]">
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold">{bot.label}</h3>
          <p className="text-xs text-white/55 mt-0.5">{bot.desc}</p>
        </div>
        {bot.comingSoon ? (
          <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-300 px-2 py-1 rounded">Coming Soon</span>
        ) : (
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <span className="text-[10px] uppercase tracking-[0.14em] text-white/55">{on ? "On" : "Off"}</span>
            <span
              onClick={() => onToggle(!on)}
              className={`relative inline-flex w-9 h-5 rounded-full transition ${on ? "bg-emerald-500/80" : "bg-white/15"}`}
              data-testid={`bots-toggle-${bot.slug}`}
              data-state={on ? "on" : "off"}
              role="switch"
              aria-checked={on}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition ${on ? "left-4" : "left-0.5"}`} />
            </span>
          </label>
        )}
      </div>
    </div>
  );
}


function RuleRow({ rule, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`rules-row-${rule.rule_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold truncate">{rule.name}</span>
          {!rule.enabled && (
            <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-white/8 text-white/55">Disabled</span>
          )}
          {rule.is_seed && (
            <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300">Default</span>
          )}
        </div>
        <div className="text-[11px] text-white/45 mt-0.5 truncate">
          <span className="font-mono">{rule.trigger?.type}</span>
          {" → "}
          <span className="font-mono">{rule.action?.type}</span>
        </div>
      </div>
      <button onClick={onEdit} className="text-white/55 hover:text-white p-1.5" data-testid={`rules-row-edit-${rule.rule_id}`}>
        <Edit3 className="w-3.5 h-3.5" />
      </button>
      <button onClick={onDelete} className="text-rose-300/65 hover:text-rose-300 p-1.5" data-testid={`rules-row-delete-${rule.rule_id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}


function RuleForm({ rule, onChange, onCancel, onSave }) {
  const availTriggers = TRIGGER_TYPES.filter((t) => t.bots.includes(rule.bot));
  const botDef = BOTS.find((b) => b.slug === rule.bot);
  const availActions = ACTION_TYPES.filter((a) => botDef?.actions.includes(a.value));

  const setT = (patch) => onChange({ ...rule, trigger: { ...rule.trigger, ...patch } });
  const setA = (patch) => onChange({ ...rule, action: { ...rule.action, ...patch } });

  return (
    <div className="space-y-4 max-w-xl">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55">
        <ChevronRight className="w-3.5 h-3.5" />
        {rule._isNew ? "New rule" : "Edit rule"} — {rule.bot}
      </div>

      <FormField label="Rule name">
        <input
          type="text" value={rule.name}
          onChange={(e) => onChange({ ...rule, name: e.target.value })}
          placeholder="e.g. Welcome message"
          maxLength={80}
          className="w-full bg-white/[0.04] border border-white/12 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#00F0FF]/40"
          data-testid="rule-form-name"
        />
      </FormField>

      <FormField label="Trigger">
        <DarkSelect
          value={rule.trigger?.type || ""}
          onChange={(v) => setT({ type: v })}
          options={availTriggers.map((t) => ({ value: t.value, label: t.label }))}
          placeholder="Select a trigger…"
          testid="rule-form-trigger"
        />
      </FormField>

      {/* Trigger args (dynamic per type) */}
      {rule.trigger?.type === "schedule" && (
        <FormField label="Every N minutes">
          <input
            type="number" min={1} max={1440}
            value={rule.trigger.minutes || 20}
            onChange={(e) => setT({ minutes: Math.max(1, parseInt(e.target.value) || 20) })}
            className="w-32 bg-white/[0.04] border border-white/12 rounded px-3 py-2 text-sm"
            data-testid="rule-form-trigger-minutes"
          />
        </FormField>
      )}
      {rule.trigger?.type === "chat_pattern" && (
        <FormField label="Pattern (regex or text)">
          <input
            type="text"
            value={rule.trigger.pattern || ""}
            onChange={(e) => setT({ pattern: e.target.value })}
            placeholder="e.g. ^!rules$"
            className="w-full bg-white/[0.04] border border-white/12 rounded px-3 py-2 text-sm font-mono"
            data-testid="rule-form-trigger-pattern"
          />
        </FormField>
      )}
      {rule.trigger?.type === "camera_state" && (
        <FormField label="Duration before action (seconds)">
          <input
            type="number" min={10} max={3600}
            value={rule.trigger.duration_sec || 60}
            onChange={(e) => setT({ duration_sec: Math.max(10, parseInt(e.target.value) || 60),
                                    states: rule.trigger.states || ["no_person", "camera_off"] })}
            className="w-32 bg-white/[0.04] border border-white/12 rounded px-3 py-2 text-sm"
            data-testid="rule-form-trigger-duration"
          />
        </FormField>
      )}

      <FormField label="Action">
        <DarkSelect
          value={rule.action?.type || ""}
          onChange={(v) => setA({ type: v })}
          options={availActions.map((a) => ({ value: a.value, label: a.label }))}
          placeholder="Select an action…"
          testid="rule-form-action"
        />
      </FormField>

      {/* Action args */}
      {(ACTION_TYPES.find((a) => a.value === rule.action?.type)?.textRequired) && (
        <FormField label="Message text">
          <textarea
            value={rule.action.text || ""}
            onChange={(e) => setA({ text: e.target.value })}
            placeholder="Welcome to {room_name}!"
            rows={3}
            className="w-full bg-white/[0.04] border border-white/12 rounded px-3 py-2 text-sm font-mono"
            data-testid="rule-form-action-text"
          />
          <p className="text-[10px] text-white/40 mt-1.5">
            Variables: <span className="font-mono">{"{user_name}"}</span> <span className="font-mono">{"{room_name}"}</span> <span className="font-mono">{"{now}"}</span> <span className="font-mono">{"{count_participants}"}</span>
          </p>
        </FormField>
      )}

      {rule.action?.type === "escalate_removal" && (
        <FormField label="Reason (shown to staff)">
          <input
            type="text"
            value={rule.action.reason || ""}
            onChange={(e) => setA({ reason: e.target.value })}
            placeholder="e.g. Camera has been off for 4+ minutes despite warning."
            className="w-full bg-white/[0.04] border border-white/12 rounded px-3 py-2 text-sm"
          />
        </FormField>
      )}

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={rule.enabled}
          onChange={(e) => onChange({ ...rule, enabled: e.target.checked })}
          className="accent-emerald-500"
          data-testid="rule-form-enabled"
        />
        Rule enabled
      </label>

      <div className="pt-3 flex items-center gap-2 border-t border-white/8">
        <button onClick={onCancel} className="px-4 py-2 text-xs uppercase tracking-[0.14em] text-white/55 hover:text-white">
          Cancel
        </button>
        <button
          onClick={onSave}
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] text-xs font-bold uppercase tracking-[0.14em]"
          data-testid="rule-form-save"
        >
          <Save className="w-3.5 h-3.5" /> Save
        </button>
      </div>
    </div>
  );
}


function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 mb-1.5 font-bold">{label}</div>
      {children}
    </label>
  );
}
